<template>
	<Accessible
		name="leavemodal"
		trapped
		focusFirstItem
		autoFocus
		:wait="100"
		:tabOrder="[
			{ css: '.modal-close-btn', clickOnEnter: true },
			{ all: '.option', clickOnEnter: true },
		]"
	>
		<SimpleModal @close="close()">
			<div class="mute-wrapper" data-cy="mutewrapper">
				<div class="title" v-text="title" />
				<div
					v-for="{ id, label } in options"
					:key="id"
					@click="selected(id)"
					class="option"
					:data-cy="`option-${id}`"
					:class="{ [`option-${id}`]: true }"
				>
					<Spinner v-if="loading && selectedOption === id" />
					<span v-else v-text="label" />
				</div>
			</div>
		</SimpleModal>
	</Accessible>
</template>

<script>
import store, { getMyMembershipForBubble, setMembership } from '@/store'
import { post } from '@/helpers/api'
import { SimpleModal, Spinner } from '@/components/common'
import moment from 'moment'
import Accessible from '@/components/common/Accessible'

export default {
	props: {
		group: { type: Boolean, default: false },
		edit: { type: Boolean, default: false },
		bubble_id: { type: Number, default: null },
		titleOverride: { type: String, default: null },
	},
	components: { SimpleModal, Spinner, Accessible },

	data() {
		return {
			store,
			success: false,
			selectedOption: null,
			loading: false,
		}
	},

	emits: ['close'],

	computed: {
		title() {
			if (this.titleOverride) return this.titleOverride
			return this.group
				? this.$t('groupsettings_notifications_mute_group')
				: this.$t('settings_option_donotdisturb')
		},
		options() {
			const opts = [
				{
					id: 20,
					label: this.$t(
						'accessibility_minutes_label.variable',
						{
							Count: 20,
						},
						20
					),
				},
				{
					id: 60,
					label: this.$t(
						'accessibility_hours_label.variable',
						{
							Count: 1,
						},
						1
					),
				},
				{
					id: 120,
					label: this.$t(
						'accessibility_hours_label.variable',
						{
							Count: 2,
						},
						2
					),
				},
				{
					id: 240,
					label: this.$t(
						'accessibility_hours_label.variable',
						{
							Count: 4,
						},
						4
					),
				},
				{
					id: 480,
					label: this.$t(
						'accessibility_hours_label.variable',
						{
							Count: 8,
						},
						8
					),
				},
				{
					id: 1440,
					label: this.$t(
						'accessibility_hours_label.variable',
						{
							Count: 24,
						},
						24
					),
				},
				// Did 9999 for sorting in the rendered list
				{
					id: 9999,
					label: this.$t('groupsettings_notifications_mute_action_indefinite'),
				},
			]
			if (this.edit) {
				opts.unshift({
					id: 0,
					label: this.$t('groupsettings_notifications_mute_turnoff'),
				})
			}
			return opts
		},
	},

	methods: {
		close() {
			this.$emit('close', { success: this.success })
		},

		createTime(minutes) {
			let today = new Date()
			var date = moment.utc(today)

			return date.add(minutes, 'minutes').format('YYYY-MM-DD HH:mm:ss')
		},

		async selected(option) {
			this.selectedOption = option
			const url = this.group
				? '/api/v1/membership.update'
				: '/api/v1/user.update'

			let payload = {
				mute: option !== 0,
				muteuntil: null,
			}

			if (option !== 0 && option !== 9999) {
				payload.muteuntil = this.createTime(option)
			}

			if (this.group) {
				payload.bubble_id = this.bubble_id
					? this.bubble_id
					: this.store.currentChatId
			}

			this.loading = true

			let membership = {}

			if (this.group) {
				membership = getMyMembershipForBubble(payload.bubble_id) || {}
			}

			try {
				if (this.group) {
					const { bubble_id, mute, muteuntil } = payload

					setMembership(
						Object.assign(membership, { bubble_id, mute, muteuntil })
					)
				}

				const { data } = await post(url, payload)

				if (!this.group) {
					data.user.blocked = this.store.auth.user.blocked
					this.store.auth.user = data.user
					this.store.auth.setUser(this.store.auth.user, this.store.userKey)
					this.success = true
					this.close()
				}
			} catch (e) {
				console.error(e)
				if (this.group) {
					setMembership(
						Object.assign(membership, {
							mute: false,
							muteuntil: null,
						})
					)
				}
			}

			if (this.group) {
				this.close()
			}

			this.loading = false
			this.selectedOption = null
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.mute-wrapper {
	width: 390px;
	text-align: center;
	color: var(--neutral-800);

	.title {
		margin-top: 20px;
		margin-bottom: 24px;
		font-family: Avenir-Heavy;
		font-size: 20px;
	}
	.option {
		cursor: pointer;
		@include flex-center;
		height: 52px;
		border-top: solid 1px var(--neutral-40040);
		&:not(.option-0):hover {
			color: var(--blue-300);
			cursor: pointer;
		}
		&.option-0 {
			color: var(--red-300);
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.mute-wrapper {
		.option {
			&:not(.option-0):hover {
				color: var(--teal-300);
			}
		}
	}
}
</style>
